import { useEffect, useRef, useState } from "react";
import { createRenderer } from "./core/createRenderer";
import { createScene } from "./core/createScene";
import { createCamera } from "./core/createCamera";
import { createControls } from "./core/createControls";
import { createPLYMesh } from "./core/createMesh";
import { createDisposers } from "./utils/createDisposers";
import { createLoop } from "./loop/createLoop";
import { bindResize } from "./events/bindResize";
import { bindPointerRaycast } from "./events/bindPointerRaycast";
import AnimationControllBox from "./AnimationControllBox";
import FileValueTable from "./FileValueTable";
import FftBarGraph2 from "./FftBarGraph2";

export default function ThreeViewer({
  plyUrl,
  textureUrl,
  frames,
  images,
  points3D,
}) {
  const mountRef = useRef(null);
  const meshRef = useRef(null);
  const isRotateAniRef = useRef(true);
  // loading progress
  const [progress, setProgress] = useState(0);
  // animation play/pause
  const [isRotateAni, setIsRotateAni] = useState(true);
  // markupState
  const [isMarked, setIsMarked] = useState(false);
  // audio play/pause
  const [isAudio, setIsAudio] = useState(false);

  console.log(frames);
  console.log(images);
  console.log(points3D);

  useEffect(() => {
    isRotateAniRef.current = isRotateAni;
  }, [isRotateAni]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    //STEP_________________0) 해제 스택 (메모리 누수 방지)_________________
    const disposers = createDisposers();

    //_________________1) core 생성 부 _________________
    // Renderer
    const renderer = createRenderer(mount);
    // Scene
    const scene = createScene({ background: 0x0f1115 });
    // Camera
    const camera = createCamera(mount);
    // Controls
    const controls = createControls(camera, renderer.domElement);

    //STEP_________________2) Mesh 로드 _________________
    let disposed = false; // 비동기 로드 중 언마운트 대비
    (async () => {
      try {
        const { mesh } = await createPLYMesh({
          plyUrl,
          textureUrl,
          setProgress, // ← onProgress 없이 바로 UI 갱신
        });
        if (disposed) return;
        meshRef.current = mesh;
        scene.add(mesh);
      } catch (e) {
        console.error(e);
      }
    })();

    //STEP_________________3) 화면에 렌더링 _________________
    //Render Loop
    //생성
    const loop = createLoop({
      renderer,
      scene,
      camera,
      controls,
      meshRef,
      isRotateAniRef,
    });
    //루프 시작
    loop.start();
    //cleanUp stack에 등록
    disposers.add(loop.stop);

    //STEP_________________4) 이벤트 바인딩 _________________
    //Resize 대응
    //cleanUp stack에 등록
    disposers.add(bindResize({ mount, camera, renderer, loop }));
    disposers.add(
      bindPointerRaycast({
        mount,
        camera,
        isRotateAni,
        meshRef,
        setIsRotateAni,
        setIsMarked,
        getTargets: () => (meshRef.current ? [meshRef.current] : []),
        options: { throttleWithRaf: true },
      })
    );

    //STEP_________________5) unmount 시 리소스 정리 _________________
    //Cleanup
    return () => {
      disposed = true;
      //모든 이벤트/루프 해제
      disposers.run();
      //컨트롤 리소스 정리
      controls.dispose();
      //three 리소스 정리
      if (meshRef.current) {
        const m = meshRef.current;
        m.geometry?.dispose?.();
        const mats = Array.isArray(m.material) ? m.material : [m.material];
        mats.forEach((mat) => {
          mat?.map?.dispose?.();
          mat?.dispose?.();
        });
        scene.remove(m);
        meshRef.current = null;
      }
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  return (
    <div className="threeContainer" ref={mountRef}>
      <div className="info">
        <AnimationControllBox
          isRotateAni={isRotateAni}
          setIsRotateAni={setIsRotateAni}
          isAudio={isAudio}
          setIsAudio={setIsAudio}
          onReset={() => {
            const mesh = meshRef.current;
            if (mesh) {
              mesh.position.set(0, 0, 0); // 위치 초기화
              mesh.rotation.set(0, 0, 0); // 회전 초기화
              mesh.updateMatrixWorld();
              mesh.rotateZ(Math.PI);
            }
          }}
        />
        <div className="value">
          {isMarked && <FftBarGraph2 />}
          {isMarked && <FileValueTable />}
        </div>
      </div>
      <p className="progressLabel">Loading... {progress.toFixed(0)}%</p>
    </div>
  );
}
